import {
  GetVideoVersionDownloadsOptions,
  GetVideoVersionDownloadsResult,
  getVideoVersionDownloads,
  GetVideoVersionDownloadsFields,
} from '../fetchers/video/version/downloads';
import useSWR, { SWRConfiguration, useSWRConfig } from 'swr';
import { assignMswData, useInternalState, serializeQuery } from '../hooks/internalState';
import { useCallback } from 'react';
import { useGctlConfig } from '../hooks/GctlProvider';

/**
 * DO NOT MODIFY
 * Auto-generated by gctl-plugin-vimeo-hooks
 */

type UseGetVideoVersionDownloadsOptions<Fields extends GetVideoVersionDownloadsFields> =
  GetVideoVersionDownloadsOptions<Fields>;

/**
 * Get a list of downloadable file links for a video with an alternate audio track
 * This method returns a list of downloadable file links for a version of a video that contains the specified alternate audio track.
 */

export function useGetVideoVersionDownloads<Fields extends GetVideoVersionDownloadsFields>(
  getOptions:
    | UseGetVideoVersionDownloadsOptions<Fields>
    | (() => UseGetVideoVersionDownloadsOptions<Fields> | null),
  config?: SWRConfiguration<GetVideoVersionDownloadsResult<Fields>>,
) {
  const options = typeof getOptions === 'function' ? getOptions() : getOptions;

  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  return useSWR(
    options
      ? `/videos/${options.where.videoId}/versions/${
          options.where.versionId
        }/downloads${serializeQuery(options)}`
      : () => null,
    options
      ? () => {
          return getVideoVersionDownloads({
            ...options,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
            baseUrl,
          });
        }
      : null,
    config,
  );
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetVideoVersionDownloads, {
    endpoint: '/videos/:videoId/versions/:versionId/downloads',
    method: 'GET',
  });
}

/**
 * Get a list of downloadable file links for a video with an alternate audio track
 * This method returns a list of downloadable file links for a version of a video that contains the specified alternate audio track.
 */

export function useGetVideoVersionDownloadsLazy<Fields extends GetVideoVersionDownloadsFields>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<GetVideoVersionDownloadsResult<Fields> | undefined>();

  let callback = useCallback(
    async (options: UseGetVideoVersionDownloadsOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/videos/${options.where.videoId}/versions/${
            options.where.versionId
          }/downloads${serializeQuery(options)}`,
          getVideoVersionDownloads({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [callback, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetVideoVersionDownloadsLazy, {
    endpoint: '/videos/:videoId/versions/:versionId/downloads',
    method: 'GET',
  });
}
